import { useCallback, useEffect, useRef, useState } from 'react';

import { stockAssistantApi } from '@/lib/api/stock-assistant.api';
import { getSupportedAudioMimeType } from '@/lib/audio-mime';
import { RATE_LIMIT_MESSAGE, serverMessage, statusOf } from '@/lib/stock-assistant-errors';

type Status = 'idle' | 'recording' | 'transcribing' | 'unsupported' | 'blocked';

interface Options {
  maxSeconds?: number;
  onText: (text: string) => void;
}

/** Prefer the server's message (axios only carries a generic one) so staff see what to do. */
const toErrorMessage = (err: unknown): string => {
  if (statusOf(err) === 429) return RATE_LIMIT_MESSAGE;
  const message = serverMessage(err);
  if (message) return message;
  return err instanceof Error ? err.message : 'Voice note failed';
};

/** Record a short voice note, transcribe it server-side, hand back the text. */
export const useVoiceNote = ({ maxSeconds = 55, onText }: Options) => {
  const mimeType = getSupportedAudioMimeType();
  const supported = !!mimeType && !!navigator.mediaDevices?.getUserMedia;
  const [status, setStatus] = useState<Status>(supported ? 'idle' : 'unsupported');
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const onTextRef = useRef(onText);
  const unmounted = useRef(false);
  const starting = useRef(false);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    onTextRef.current = onText;
  }, [onText]);

  const clearTimer = useCallback(() => {
    if (timer.current) clearInterval(timer.current);
    timer.current = null;
  }, []);

  const stop = useCallback(() => {
    clearTimer();
    if (recorder.current?.state === 'recording') recorder.current.stop();
  }, [clearTimer]);

  const start = useCallback(async () => {
    if (!supported || !mimeType || starting.current || recorder.current?.state === 'recording') {
      return;
    }
    starting.current = true;
    setIsStarting(true);
    setError(null);
    let media: MediaStream;
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      starting.current = false;
      if (unmounted.current) return;
      setIsStarting(false);
      if ((err as Error).name === 'NotAllowedError') {
        setStatus('blocked');
      } else {
        setStatus('idle');
        setError('Could not start the microphone');
      }
      return;
    }
    starting.current = false;
    if (unmounted.current) {
      setIsStarting(false);
      media.getTracks().forEach((t) => t.stop());
      return;
    }
    setIsStarting(false);
    stream.current = media;
    const chunks: Blob[] = [];
    let rec: MediaRecorder;
    try {
      rec = new MediaRecorder(media, { mimeType });
    } catch {
      media.getTracks().forEach((t) => t.stop());
      stream.current = null;
      setStatus('idle');
      setError('Could not start the microphone');
      return;
    }
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    rec.onstop = async () => {
      media.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks, { type: mimeType });
      if (blob.size === 0) {
        setError("Didn't catch that — try again");
        setStatus('idle');
        return;
      }
      setStatus('transcribing');
      try {
        const { text } = await stockAssistantApi.transcribe(blob);
        if (text.trim()) onTextRef.current(text.trim());
        else setError("Didn't catch that — try again");
      } catch (err) {
        setError(toErrorMessage(err));
      } finally {
        setStatus('idle');
      }
    };
    recorder.current = rec;
    try {
      rec.start();
    } catch {
      media.getTracks().forEach((t) => t.stop());
      stream.current = null;
      recorder.current = null;
      setStatus('idle');
      setError('Could not start the microphone');
      return;
    }
    setSeconds(0);
    setStatus('recording');
    let elapsed = 0;
    timer.current = setInterval(() => {
      elapsed += 1;
      setSeconds(elapsed);
      if (elapsed >= maxSeconds) stop();
    }, 1000);
  }, [maxSeconds, mimeType, stop, supported]);

  // On unmount: drop the result and release the microphone.
  useEffect(() => {
    // Reset on (re)mount so React StrictMode's mount, cleanup, mount cycle keeps working.
    unmounted.current = false;
    return () => {
      unmounted.current = true;
      clearTimer();
      const rec = recorder.current;
      if (rec) {
        rec.onstop = null;
        if (rec.state === 'recording') rec.stop();
      }
      stream.current?.getTracks().forEach((t) => t.stop());
    };
  }, [clearTimer]);

  return { status, seconds, error, start, stop, supported, starting: isStarting };
};
